import * as cx from "classnames/bind";
import {
  Button,
  ButtonLink,
  Container,
  CreateListModal,
  CreateReviewModal,
  Section,
  Icon,
  Stars,
  ShareModal,
} from "components";
import OutsideClickHandler from "react-outside-click-handler";
import { default as CocktailComponent } from "components/atoms/Cocktail";
import { useRecipeLists, useSession } from "hooks";
import { useRelatedCocktails } from "hooks/algolia";
import { HomeLayout } from "layouts";
import Head from "next/head";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { runQuery } from "queries";
import { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import s from "styles/Cocktail.module.scss";
import r from "styles/reviewStars.module.scss";
import {
  imageUrl,
  uniqObjArrayOld as uniqObjArray,
  uniqObjArray as uniqObjArrayNew,
  combineIngredients,
  translate,
} from "utils";
import store from "store2";
import { push } from "@socialgouv/matomo-next";
import Cookies from "js-cookie";

const ProductDetailModal = ({ open, product = {}, onClose }) => {
  const router = useRouter();
  const locale = router.locale;
  const description = translate(product, "description", locale);
  const parent = product?.parent?.parent || product?.parent || product.parent;
  const country = (Cookies.get("__country") || "no").toLowerCase();
  const [productDetailState, setProductDetailState] = useState({ open: false });
  const externalLinks =
    (product?.extra_data?.external_links || {})[country] || [];
  return open ? (
    <div className={cx(s.productDetailModal)}>
      <a
        className={cx(s.productDetailCloseBtn)}
        onClick={onClose}
        role="button"
        tabIndex="0"
      >
        <img alt="Close" src="/img/icons/close.svg" />
      </a>
      <img className="Rad" src={imageUrl(product.images[0])} />
      <div className={cx(s.pdmTitle)}>{product.name}</div>
      {description && <p>{description}</p>}
      <small className={cx(s.pdmCategory)}>
        Category: <span>{parent.name}</span>
      </small>
      <small className={cx(s.pdmCategory)}>
        Used in:{" "}
        <span className={cx(s.pdmSmallLink)}>
          {product.numCocktails} cocktails
        </span>
      </small>
      {externalLinks.map
        ? (externalLinks || []).map((link, index) => (
            <Link
              className={cx("Button Primary Cyan Hollow mb8", s.pdmButton)}
              key={index}
              href={link}
              target="_blank"
            >
              Buy product
            </Link>
          ))
        : null}
      <Link
        className={cx("Button Primary Cyan", s.pdmButton)}
        href={`/products/${product.slug}`}
      >
        View product
      </Link>
    </div>
  ) : null;
};

const RelatedCocktails = ({
  cocktail,
  setShowCreateListModal,
  setLoginModalState,
  ...props
}) => {
  const { t } = useTranslation();
  const { related } = useRelatedCocktails(cocktail?.id);
  return (
    <Section>
      <Container className="mt32">
        <h2 className="Heading">{t("Related cocktails")}</h2>
        <div className={cx("GalleryFeed row", s.GalleryFeed)}>
          {(related || []).map((cocktail) => (
            <CocktailComponent
              key={cocktail.id}
              cocktail={cocktail}
              setShowCreateListModal={setShowCreateListModal}
              setLoginModalState={setLoginModalState}
              {...props}
            />
          ))}
        </div>
      </Container>
    </Section>
  );
};

export const CocktailPage = ({
  cocktail = { recipes: [], translations: {}, extra_data: {} },
  setShowLoginModal,
  setLoginModalState,
  horeca = false,
  event = false,
  ...props
}) => {
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showCreateReviewModal, setShowCreateReviewModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [noServings, setNoServings] = useState(1);
  const [measurementSystem, setMeasurementSystem] = useState("metric");
  const { t } = useTranslation();
  const router = useRouter();
  const locale = router.locale;

  const { data: session, status } = useSession();

  const country = (Cookies.get("__country") || "no").toLowerCase();
  const tickets = session?.user?.extraData?.tickets || [];
  const userHasEnteredEvent = session?.user && tickets.includes(country);

  useEffect(() => {
    if (
      cocktail?.extra_data?.event === true &&
      !userHasEnteredEvent &&
      status != "loading"
    ) {
      setLoginModalState({
        show: true,
        event: true,
        message: t("Win a trip to Barcelona by signing up!"),
        index: session?.user ? 2 : 1,
      });
    }
  }, [cocktail, setLoginModalState, t, status, userHasEnteredEvent, session]);

  const getDescription = (cocktail) =>
    translate(cocktail, "description", locale) || "";

  const [description, setDescription] = useState(getDescription(cocktail));

  const shareUrl = `https://cocktailclub.com/cocktails/${cocktail.slug}`;
  const handleShare = async () => {
    if (navigator.share) {
      // Native sharing is possible
      navigator
        .share({
          title: `How to make "${cocktail.name}" - Cocktail Club`,
          url: `${shareUrl}`,
        })
        .then(() => {
          console.log("Native sharing was successful");
        })
        .catch((error) => {
          console.error("There was an error with native sharing:", error);
        });
    } else {
      // Native sharing is not possible, so we set the state
      setShowShareModal(true);
    }
  };

  const [activeRecipe, setActiveRecipe] = useState(
    cocktail
      ? cocktail.recipes.find((r) => r.default) || cocktail.recipes[0]
      : null,
  );

  const isSimple = (activeRecipe?.timeline?.steps || []).length < 2;

  const [forceLogin, setForceLogin] = useState(false);

  const watchedVideos = store("watchedVideos") || [];
  useEffect(() => {
    if (cocktail) {
      setActiveRecipe(
        cocktail.recipes.find((r) => r.default) || cocktail.recipes[0],
      );
    }
  }, [cocktail]);

  useEffect(() => {
    if (cocktail) {
      setDescription(getDescription(cocktail));
    }
  }, [cocktail]);

  useEffect(() => {
    if (activeRecipe) {
      const force = session?.user
        ? false
        : watchedVideos.indexOf(activeRecipe.timeline) > -1
        ? false
        : watchedVideos.length > 0;
      setForceLogin(force);
    }
  }, [session, activeRecipe]);

  const { recipeLists, setRecipeLists } = useRecipeLists([]);
  const isInList = !!recipeLists
    .flatMap((list) => list.items)
    .find((cId) => cId == cocktail.id);

  const [isShown, setIsShown] = useState(false);

  const toggle = () => {
    setIsShown(!isShown);
  };

  const [productDetailState, setProductDetailState] = useState({ open: false });

  const toggleRecipeList = async (selected, list) => {
    const newLists = recipeLists.map((l) => {
      return l.name != list.name
        ? l
        : {
            ...l,
            items: l.items
              .filter((cId) => cId != cocktail.id)
              .concat(selected ? [cocktail.id] : []),
          };
    });
    await setRecipeLists(newLists);
    setIsShown(false);
  };

  const trackEvent = () => {
    push(["trackEvent", "Cocktails", "Click play recipe", "Play recipe"]);
  };
  useEffect(() => {
    push(["trackEvent", "Cocktails", "View cocktail recipe", "View recipe"]);
  }, []);

  const image_url = imageUrl((cocktail || {}).image?.id || "");
  const regex = /(<([^>]+)>)/gi;

  const keywords = activeRecipe
    ? uniqObjArray(activeRecipe.timeline.steps.flatMap((s) => s.ingredients))
        .map((ingredient) => {
          return `${ingredient.name}`;
        })
        .join(",")
    : "";

  const recipeIngredients = activeRecipe
    ? uniqObjArray(
        activeRecipe.timeline.steps.flatMap((s) => s.ingredients),
      ).map((ingredient) => {
        const measurement = ingredient.measurements[0] || {};
        const metricAmount =
          measurement.metric_amount !== ""
            ? parseFloat(measurement.metric_amount || 1) * noServings
            : "";
        return `"${metricAmount} ${
          (measurement.metric_unit || {}).name || ""
        } ${ingredient.name}"`.trim();
      })
    : [];

  const recipeInstructions = activeRecipe
    ? activeRecipe.timeline.steps.map((step) => ({
        "@type": "HowToStep",
        name: step.title,
        text: step.description || step.title,
      }))
    : [];

  const ldJsonCocktail =
    cocktail && activeRecipe && activeRecipe.timeline.wistia_meta && !horeca
      ? {
          "@context": "https://schema.org",
          "@id": `https://cocktailclub.com/cocktails/${cocktail.slug}`,
          recipeCuisine: "drinks",
          "@type": "Recipe",
          description: `${description.replace(regex, "")}`,
          image: {
            "@type": "ImageObject",
            url: `${activeRecipe.timeline.wistia_meta.thumbnail.url.replace(
              /\d+x\d+/,
              "1280x800",
            )}`,
            caption: `${cocktail.name} Video Recipe - Cocktail Club`,
            width: 1280,
            height: 800,
          },
          video: {
            "@type": "VideoObject",
            thumbnail: `${activeRecipe.timeline.wistia_meta.thumbnail.url.replace(
              /\d+x\d+/,
              "1280x800",
            )}`,
            thumbnailUrl: `${activeRecipe.timeline.wistia_meta.thumbnail.url.replace(
              /\d+x\d+/,
              "1280x800",
            )}`,
            name: `${cocktail.name} Video Recipe - Cocktail Club`,
            description: `${description.replace(regex, "")}`,
            contentUrl: `${activeRecipe.timeline.wistia_meta.assets[0].url.replace(
              ".bin",
              ".mp4",
            )}`,
            embedUrl: `https://fast.wistia.com/embed/iframe/${activeRecipe.timeline.video_embed_id}?version=v1&controlsVisibleOnLoad=true&playerColor=16212a`,
            author: "Cocktail Club",
            genre: "Howto & Style",
            width: 1280,
            height: 800,
            uploadDate: `${activeRecipe.timeline.wistia_meta.created}`,
            duration: `${
              activeRecipe && activeRecipe.timeline.duration
                ? `PT${activeRecipe.timeline.duration}M`
                : ""
            }`,
            creator: {
              "@type": "Organization",
              name: "Cocktail CLub",
              url: "https://cocktailclub.com",
              logo: {
                "@type": "ImageObject",
                url: "https://cocktailclub.com/img/cc_logo-blue.png",
                width: 655,
                height: 100,
              },
            },
          },
          ...(cocktail.reviews.length
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  worstRating: 1,
                  bestRating: 5,
                  ratingValue: `${cocktail.averageScore}`,
                  ratingCount: `${cocktail.reviews.length}`,
                },
              }
            : {}),
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://cocktailclub.com/cocktails/${cocktail.slug}`,
          },
          name: `${cocktail.name}`,
          url: `https://cocktailclub.com/cocktails/${cocktail.slug}`,
          headline: `${cocktail.name}`,
          author: {
            "@type": "Organization",
            name: "Cocktail Club",
          },
          dateModified: `${activeRecipe.timeline.wistia_meta.updated}`,
          datePublished: `${activeRecipe.timeline.wistia_meta.created}`,

          keywords: [`${keywords}, Cocktail, Drink, Recipe, Video, How to`],

          publisher: {
            "@type": "Organization",
            name: "Cocktail CLub",
            url: "https://cocktailclub.com",
            logo: {
              "@type": "ImageObject",
              url: "https://cocktailclub.com/img/cc_logo-blue.png",
              width: 655,
              height: 100,
            },
          },
          recipeIngredient: recipeIngredients,
          recipeInstructions: recipeInstructions,
          recipeCategory: "Cocktail",
          recipeYield: 1,
          cookTime: `${
            activeRecipe && activeRecipe.timeline.wistia_meta.duration
              ? `PT${activeRecipe.timeline.wistia_meta.duration / 2}S`
              : ""
          }`,
          prepTime: `${
            activeRecipe && activeRecipe.timeline.wistia_meta.duration
              ? `PT${activeRecipe.timeline.wistia_meta.duration / 2}S`
              : ""
          }`,
          totalTime: `${
            activeRecipe && activeRecipe.timeline.wistia_meta.duration
              ? `PT${activeRecipe.timeline.wistia_meta.duration}S`
              : ""
          }`,
        }
      : {};
  const [isModalOpen, setModalOpen] = useState(false);

  const openProductDetailPopup = (product) => {
    if (!product.isSpecific) {
      return;
    }
    setProductDetailState({ open: true, product });
  };
  return cocktail && activeRecipe ? (
    <>
      {!horeca && (
        <Head>
          <title key="title">
            {t("How to make")} {cocktail.name} - Cocktail Club
          </title>
          <meta
            key="description"
            name="description"
            content={cocktail.description.replace(regex, "")}
          />
          <meta key="ogImage" property="og:image" content={image_url} />
          <meta
            property="og:image:alt"
            content={cocktail.name + " - Cocktail Club"}
          />
          <meta
            key="ogUrl"
            property="og:url"
            content={"https://cocktailclub.com/cocktails/" + cocktail.slug}
          />
          <link
            key="canonical"
            rel="canonical"
            href={"https://cocktailclub.com/cocktails/" + cocktail.slug}
          />

          <meta key="ogType" property="og:type" content="article" />
          <meta
            key="ogTitle"
            property="og:title"
            content={
              "How to make " + cocktail.name + " - video recipe | Cocktail Club"
            }
          />
          <meta
            key="ogDescription"
            property="og:description"
            content={cocktail.description.replace(regex, "")}
          />

          <meta property="article:section" content="Cocktails" />
          <meta
            property="article:tag"
            content={cocktail.name + " - Cocktail Club"}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonCocktail) }}
          />
        </Head>
      )}
      {cocktail?.extra_data?.event === true &&
        !userHasEnteredEvent &&
        status != "loading" && (
          <div className={cx(s.enterCompHolder, "enterCompHolder")}>
            <button
              enterCompBtn="true"
              className={cx(s.enterCompBtn, "Primary Button Cyan enterCompBtn")}
              onClick={() =>
                setLoginModalState({
                  show: true,
                  event: true,
                  message: t("Win a trip to Barcelona by signing up!"),
                  index: session?.user ? 2 : 1,
                })
              }
            >
              Enter competition
            </button>
          </div>
        )}
      <article className="b-white">
        <Section className="p0">
          <Container className="fullWidth">
            <div className="flex flex-wrap">
              <div className={cx("split-item-50__d", s.Preview)}>
                <div>
                  <button
                    backBtn="true"
                    className={cx("backBtn", s.backBtn)}
                    onClick={() => router.back()}
                  ></button>
                  {session?.user ? (
                    <button
                      favBtn="true"
                      className={cx(
                        "favBtn",
                        s.favBtn,
                        isInList ? "in-list" : "",
                      )}
                      onClick={toggle}
                    ></button>
                  ) : (
                    <button
                      favBtn="true"
                      className={cx("favBtn", s.favBtn)}
                      onClick={() =>
                        setLoginModalState({
                          show: true,
                          message: t(
                            "Please log in or register an account to create menus",
                          ),
                          index: store("hasLoggedIn") ? 0 : 1,
                        })
                      }
                    ></button>
                  )}
                  <div
                    className={cx("favList", s.favList, isShown ? "open" : "")}
                  >
                    <div
                      className={cx("favListOverlay", s.favListOverlay)}
                      onClick={toggle}
                    ></div>
                    <div className={cx("favListHeader", s.favListHeader)}>
                      <b>
                        <span>{cocktail.name} - </span>
                        {t("Add to menu")}:
                      </b>
                      <button
                        className={cx("closeBtn", s.closeBtn)}
                        onClick={toggle}
                        title={t("Close")}
                      ></button>
                    </div>
                    <ul>
                      {recipeLists.map((list, index) => (
                        <li key={index} className="checkContainer">
                          <label>
                            <input
                              type="checkbox"
                              checked={
                                !!list.items.find((cId) => cId == cocktail.id)
                              }
                              onChange={(e) =>
                                toggleRecipeList(e.target.checked, list)
                              }
                            />
                            {list.name}
                          </label>
                        </li>
                      ))}
                      {recipeLists.length > 0 ? (
                        <></>
                      ) : (
                        <li className="checkContainer">
                          <label>{t("Your menu list is empty.")}</label>
                        </li>
                      )}
                    </ul>
                    <Button
                      className={cx(
                        "Full Rad0 noFocus",
                        recipeLists.length > 0 ? "" : "Bold Cyan",
                      )}
                      onClick={() => setShowCreateListModal(true)}
                    >
                      {t("Create menu")}&nbsp;+
                    </Button>
                  </div>
                  {activeRecipe.timeline.video_embed_id &&
                  activeRecipe.timeline.video_embed_id != "null" ? (
                    activeRecipe.timeline.video_embed_id.match(
                      /^(https?:\/\/|www\.)((youtube\.com|youtu\.be|vimeo\.com)\/)/i,
                    ) ? (
                      <Link
                        href={activeRecipe.timeline.video_embed_id}
                        prefetch={false}
                        target="_blank"
                        onClick={(e) => {
                          trackEvent();
                          if (forceLogin) {
                            e.preventDefault();
                            setLoginModalState({
                              show: true,
                              message: t(
                                "Please log in or register an account to watch more video recipes",
                              ),
                              index: 0,
                            });
                          }
                        }}
                      >
                        <figure
                          itemProp="image"
                          itemScope=""
                          itemType="http://schema.org/ImageObject"
                        >
                          {cocktail.image && (
                            <Image
                              src={image_url}
                              width={1080}
                              height={1080}
                              alt={cocktail.name}
                              fill="intrinsic"
                              objectFit="cover"
                              itemProp="image"
                            />
                          )}
                        </figure>
                      </Link>
                    ) : (
                      <Link
                        href={`${router.asPath}/play`}
                        prefetch={false}
                        onClick={(e) => {
                          trackEvent();
                          if (forceLogin) {
                            e.preventDefault();
                            setLoginModalState({
                              show: true,
                              message: t(
                                "Please log in or register an account to watch more video recipes",
                              ),
                              index: 0,
                            });
                          }
                        }}
                      >
                        <figure
                          itemProp="image"
                          itemScope=""
                          itemType="http://schema.org/ImageObject"
                        >
                          {cocktail.image && (
                            <Image
                              src={image_url}
                              width={1080}
                              height={1080}
                              alt={cocktail.name}
                              fill="intrinsic"
                              objectFit="cover"
                              itemProp="image"
                            />
                          )}
                        </figure>
                      </Link>
                    )
                  ) : (
                    <figure
                      itemProp="image"
                      itemScope=""
                      itemType="http://schema.org/ImageObject"
                    >
                      {cocktail.image && (
                        <Image
                          src={image_url}
                          width={1080}
                          height={1080}
                          alt={cocktail.name}
                          fill="intrinsic"
                          objectFit="cover"
                          itemProp="image"
                        />
                      )}
                    </figure>
                  )}
                  <header style={{ pointerEvents: "none" }}>
                    <h1 className="title" itemProp="name">
                      {cocktail.name}
                    </h1>
                    <span>
                      {activeRecipe && activeRecipe.timeline.duration
                        ? `${activeRecipe.timeline.duration} mins / `
                        : ""}
                      {(cocktail.difficulty || {}).name || ""}
                    </span>
                  </header>
                </div>
              </div>
              <div className={cx("split-item-50__d", s.Content)}>
                <div className={cx("hidden", s.Recipes)}>
                  <div>
                    <p>{t("Recipes")}: </p>
                    <ul>
                      {cocktail.recipes.map((r) => {
                        return (
                          <li key={r.id}>
                            <span
                              className={
                                activeRecipe.id == r.id
                                  ? cx("active", s.active)
                                  : ""
                              }
                              onClick={() => setActiveRecipe(r)}
                            >
                              {r.default ? "Classic" : r.name}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
                <Tabs className={cx("Tabs", s.Tabs)}>
                  <div className={cx("TabsNav", s.TabsNav)}>
                    <TabList>
                      <Tab>
                        <span>{t("Ingredients")}</span>
                      </Tab>
                      {!isSimple ? (
                        <>
                          <Tab>
                            <span>{t("Step-by-step")}</span>
                          </Tab>
                          <Tab>
                            <span>{t("Tools")}</span>
                          </Tab>
                        </>
                      ) : (
                        <></>
                      )}
                    </TabList>
                  </div>
                  <div className={cx("TabsContent", s.TabsContent)}>
                    <TabPanel className={cx("TabPane", s.TabPane)}>
                      <div className={cx("TabContainer", s.TabContainer)}>
                        <div className={cx(s.IngredientsTools)}>
                          <div>
                            <span>{t("Servings")}</span>
                            <div>
                              {[t("One"), t("Two")].map((label, index) => {
                                return (
                                  <label key={index}>
                                    <input
                                      type="radio"
                                      name="servings"
                                      value="single"
                                      defaultChecked={index + 1 == noServings}
                                      onChange={() => setNoServings(index + 1)}
                                    />
                                    <span>{label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <span>{t("Measurement")}</span>
                            <div>
                              <label>
                                <input
                                  type="radio"
                                  name="units"
                                  value="metric"
                                  defaultChecked={measurementSystem == "metric"}
                                />
                                <span
                                  onClick={() => setMeasurementSystem("metric")}
                                >
                                  ml
                                </span>
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name="units"
                                  value="imperial"
                                  defaultChecked={
                                    measurementSystem == "imperial"
                                  }
                                />
                                <span
                                  onClick={() =>
                                    setMeasurementSystem("imperial")
                                  }
                                >
                                  oz
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={cx("TabContainer", s.TabContainer)}>
                        {isSimple ? (
                          <div className={cx(s.Steps)}>
                            {activeRecipe &&
                              activeRecipe.timeline.steps.map((step, index) => {
                                return (
                                  <p key={index}>
                                    {translate(step, "description", locale)}
                                  </p>
                                );
                              })}
                          </div>
                        ) : (
                          <></>
                        )}
                        <div className={cx(s.Ingredients)}>
                          {combineIngredients(
                            uniqObjArrayNew(
                              activeRecipe.timeline.steps.flatMap(
                                (s) => s.ingredients,
                              ),
                            ),
                          ).map((ingredient) => {
                            const measurement =
                              ingredient.measurements[0] || {};
                            const metricAmount = measurement.metric_amount
                              ? parseFloat(measurement.metric_amount || 1) *
                                noServings
                              : "";
                            const imperialAmount =
                              measurement.imperial_amount ||
                              measurement.metric_amount
                                ? parseFloat(
                                    measurement.imperial_amount ||
                                      measurement.metric_amount ||
                                      1,
                                  ) * noServings
                                : "";
                            return (
                              <div
                                key={ingredient.id}
                                itemProp="recipeIngredient"
                                className={cx(
                                  s.ingredientListitem,
                                  ingredient.isSpecific && s.specific,
                                )}
                                onClick={() =>
                                  openProductDetailPopup(ingredient)
                                }
                              >
                                {measurementSystem == "metric" ? (
                                  <>
                                    <span
                                      data-amount={metricAmount}
                                      data-system="metric"
                                    >
                                      {metricAmount}
                                    </span>
                                    <span data-system="metric">
                                      {(measurement.metric_unit || {}).name ||
                                        ""}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span
                                      data-amount={imperialAmount}
                                      data-system="imperial"
                                    >
                                      {imperialAmount}
                                    </span>
                                    <span data-system="imperial">
                                      {(measurement.imperial_unit || {}).name ||
                                        (measurement.metric_unit || {}).name ||
                                        ""}
                                    </span>
                                  </>
                                )}
                                <span className={cx(s.ingredientTitle)}>
                                  {translate(ingredient, "name", locale)}{" "}
                                </span>
                                <a
                                  className="hidden"
                                  target="_blank"
                                  href={"/products?s=" + `${ingredient.name}`}
                                  rel="noreferrer"
                                >
                                  i
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {horeca &&
                        (cocktail.category?.name ||
                          cocktail.glass?.name ||
                          cocktail.occasion?.name ||
                          cocktail.difficulty?.name ||
                          cocktail.brand?.name) && (
                          <div className="py24">
                            <h2 className="Heading__small Bold">Specs</h2>

                            <ul>
                              {cocktail.category?.name && (
                                <li>Category: {cocktail.category.name}</li>
                              )}
                              {cocktail.glass?.name && (
                                <li>Glass: {cocktail.glass.name}</li>
                              )}
                              {cocktail.occasion?.name && (
                                <li>Occasion: {cocktail.occasion.name}</li>
                              )}
                              {cocktail.difficulty?.name && (
                                <li>Difficulty: {cocktail.difficulty.name}</li>
                              )}
                              {cocktail.brand?.name && (
                                <li>Brand: {cocktail.brand.name}</li>
                              )}
                            </ul>
                          </div>
                        )}
                      {!horeca && (
                        <div className="shareHolder">
                          <Button
                            svg="share"
                            size="fal"
                            className={cx("Button Blue")}
                            onClick={handleShare}
                          >
                            {t("Share cocktail")}
                          </Button>
                          <ShareModal
                            onClose={() => setShowShareModal(false)}
                            show={showShareModal}
                            shareUrl={shareUrl}
                            shareTitle={`How to make "${cocktail.name}" - Cocktail Club`}
                          />
                          {/*
                        <a
                          className="shareBtn"
                          id="shareBtnSMS"
                          href={`sms://;?&body=https://cocktailclub.com/cocktails/${cocktail.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Share with SMS"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                          >
                            <path d="M448-.0004H64c-35.25 0-64 28.75-64 63.1v287.1c0 35.25 28.75 63.1 64 63.1h96v83.98c0 9.75 11.25 15.45 19.12 9.7L304 415.1H448c35.25 0 64-28.75 64-63.1V63.1C512 28.75 483.3-.0004 448-.0004zM167.3 239.9C163.9 259.1 146.3 272 121.1 272c-4.031 0-8.25-.3125-12.59-1C101.1 269.8 92.81 266.8 85.5 264.1c-8.312-3-14.06-12.66-11.09-20.97S85 229.1 93.38 232.9c6.979 2.498 14.53 5.449 20.88 6.438C125.7 241.1 135 239 135.8 234.4c1.053-5.912-10.84-8.396-24.56-12.34c-12.12-3.531-44.28-12.97-38.63-46c4.062-23.38 27.31-35.91 58-31.09c5.906 .9062 12.44 2.844 18.59 4.969c8.344 2.875 12.78 12 9.906 20.34C156.3 178.7 147.2 183.1 138.8 180.2c-4.344-1.5-8.938-2.938-13.09-3.594c-11.22-1.656-20.72 .4062-21.5 4.906C103.2 187.2 113.6 189.5 124.4 192.6C141.4 197.5 173.1 206.5 167.3 239.9zM320 256c0 8.844-7.156 16-16 16S288 264.8 288 256V208l-19.19 25.59c-6.062 8.062-19.56 8.062-25.62 0L224 208V256c0 8.844-7.156 16-16 16S192 264.8 192 256V160c0-6.875 4.406-13 10.94-15.19c6.5-2.094 13.72 .0625 17.88 5.594L256 197.3l35.19-46.94c4.156-5.531 11.41-7.656 17.88-5.594C315.6 147 320 153.1 320 160V256zM439.3 239.9C435.9 259.1 418.3 272 393.1 272c-4.031 0-8.25-.3125-12.59-1c-8.25-1.25-16.56-4.25-23.88-6.906c-8.312-3-14.06-12.66-11.09-20.97s10.59-13.16 18.97-10.19c6.979 2.498 14.53 5.449 20.88 6.438c11.44 1.719 20.78-.375 21.56-4.938c1.053-5.912-10.84-8.396-24.56-12.34c-12.12-3.531-44.28-12.97-38.63-46c4.031-23.38 27.25-35.91 58-31.09c5.906 .9062 12.44 2.844 18.59 4.969c8.344 2.875 12.78 12 9.906 20.34c-2.875 8.344-11.94 12.81-20.34 9.906c-4.344-1.5-8.938-2.938-13.09-3.594c-11.19-1.656-20.72 .4062-21.5 4.906C375.2 187.2 385.6 189.5 396.4 192.6C413.4 197.5 445.1 206.5 439.3 239.9z" />
                          </svg>
                        </a>

                        <a
                          className="shareBtn"
                          id="shareBtnFB"
                          href={`https://facebook.com/sharer/sharer.php?u=https%3A%2F%2Fcocktailclub.com%2Fcocktails/${cocktail.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Share on Facebook"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                          </svg>
                        </a>

                        <a
                          className="shareBtn"
                          id="shareBtnTwitter"
                          href={`https://twitter.com/intent/tweet/?text=${cocktail.name} - Cocktail Club&url=https://cocktailclub.com/cocktails/${cocktail.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Share on Twitter"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M23.44 4.83c-.8.37-1.5.38-2.22.02.93-.56.98-.96 1.32-2.02-.88.52-1.86.9-2.9 1.1-.82-.88-2-1.43-3.3-1.43-2.5 0-4.55 2.04-4.55 4.54 0 .36.03.7.1 1.04-3.77-.2-7.12-2-9.36-4.75-.4.67-.6 1.45-.6 2.3 0 1.56.8 2.95 2 3.77-.74-.03-1.44-.23-2.05-.57v.06c0 2.2 1.56 4.03 3.64 4.44-.67.2-1.37.2-2.06.08.58 1.8 2.26 3.12 4.25 3.16C5.78 18.1 3.37 18.74 1 18.46c2 1.3 4.4 2.04 6.97 2.04 8.35 0 12.92-6.92 12.92-12.93 0-.2 0-.4-.02-.6.9-.63 1.96-1.22 2.56-2.14z" />
                          </svg>
                        </a>

                        <a
                          className="shareBtn"
                          id="shareBtnMail"
                          href={`mailto:?subject=${cocktail.name} - Cocktail Club&body=https%3A%2F%2Fcocktailclub.com%2Fcocktails%2F${cocktail.slug}`}
                          target="_self"
                          rel="noopener"
                          aria-label="Share by Email"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M22 4H2C.9 4 0 4.9 0 6v12c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM7.25 14.43l-3.5 2c-.08.05-.17.07-.25.07-.17 0-.34-.1-.43-.25-.14-.24-.06-.55.18-.68l3.5-2c.24-.14.55-.06.68.18.14.24.06.55-.18.68zm4.75.07c-.1 0-.2-.03-.27-.08l-8.5-5.5c-.23-.15-.3-.46-.15-.7.15-.22.46-.3.7-.14L12 13.4l8.23-5.32c.23-.15.54-.08.7.15.14.23.07.54-.16.7l-8.5 5.5c-.08.04-.17.07-.27.07zm8.93 1.75c-.1.16-.26.25-.43.25-.08 0-.17-.02-.25-.07l-3.5-2c-.24-.13-.32-.44-.18-.68s.44-.32.68-.18l3.5 2c.24.13.32.44.18.68z" />
                          </svg>
                        </a>

                        <a
                          className="shareBtn"
                          id="shareBtnPin"
                          href={`https://pinterest.com/pin/create/button/?url=https%3A%2F%2Fcocktailclub.com%2Fcocktails%2F${cocktail.slug}&amp;media=https%3A%2F%2Fcocktailclub.com%2Fcocktails%2F${cocktail.slug}&description=${cocktail.name} - Cocktail Club&note=${cocktail.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Share on Pinterest"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12.14.5C5.86.5 2.7 5 2.7 8.75c0 2.27.86 4.3 2.7 5.05.3.12.57 0 .66-.33l.27-1.06c.1-.32.06-.44-.2-.73-.52-.62-.86-1.44-.86-2.6 0-3.33 2.5-6.32 6.5-6.32 3.55 0 5.5 2.17 5.5 5.07 0 3.8-1.7 7.02-4.2 7.02-1.37 0-2.4-1.14-2.07-2.54.4-1.68 1.16-3.48 1.16-4.7 0-1.07-.58-1.98-1.78-1.98-1.4 0-2.55 1.47-2.55 3.42 0 1.25.43 2.1.43 2.1l-1.7 7.2c-.5 2.13-.08 4.75-.04 5 .02.17.22.2.3.1.14-.18 1.82-2.26 2.4-4.33.16-.58.93-3.63.93-3.63.45.88 1.8 1.65 3.22 1.65 4.25 0 7.13-3.87 7.13-9.05C20.5 4.15 17.18.5 12.14.5z" />
                          </svg>
                        </a>

                        <a
                          className="shareBtn"
                          id="shareBtnWA"
                          href={`whatsapp://send?text=${cocktail.name} - %20https%3A%2F%2Fcocktailclub.com/cocktails/${cocktail.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Share on Whatsapp"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M20.1 3.9C17.9 1.7 15 .5 12 .5 5.8.5.7 5.6.7 11.9c0 2 .5 3.9 1.5 5.6L.6 23.4l6-1.6c1.6.9 3.5 1.3 5.4 1.3 6.3 0 11.4-5.1 11.4-11.4-.1-2.8-1.2-5.7-3.3-7.8zM12 21.4c-1.7 0-3.3-.5-4.8-1.3l-.4-.2-3.5 1 1-3.4L4 17c-1-1.5-1.4-3.2-1.4-5.1 0-5.2 4.2-9.4 9.4-9.4 2.5 0 4.9 1 6.7 2.8 1.8 1.8 2.8 4.2 2.8 6.7-.1 5.2-4.3 9.4-9.5 9.4zm5.1-7.1c-.3-.1-1.7-.9-1.9-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1s-1.2-.5-2.3-1.4c-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6s.3-.3.4-.5c.2-.1.3-.3.4-.5.1-.2 0-.4 0-.5C10 9 9.3 7.6 9 7c-.1-.4-.4-.3-.5-.3h-.6s-.4.1-.7.3c-.3.3-1 1-1 2.4s1 2.8 1.1 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.3-.3-.4-.6-.5z" />
                          </svg>
                        </a>
                        */}
                        </div>
                      )}
                    </TabPanel>

                    {!isSimple ? (
                      <>
                        <TabPanel className={cx("TabPane", s.TabPane)}>
                          <div className={cx("TabContainer", s.TabContainer)}>
                            <div className={cx(s.Steps)}>
                              {activeRecipe &&
                                activeRecipe.timeline.steps.map(
                                  (step, index) => {
                                    return (
                                      <div
                                        key={index}
                                        itemProp="recipeInstructions"
                                      >
                                        <h3>
                                          <span>{index + 1}</span>
                                          {translate(step, "title", locale)}
                                        </h3>
                                        <p>
                                          {translate(
                                            step,
                                            "description",
                                            locale,
                                          )}
                                        </p>
                                        {step.ingredients.length > 0 && (
                                          <ul
                                            className={cx(
                                              "StepIngreds",
                                              s.StepIngreds,
                                            )}
                                          >
                                            <span>{t("Ingredients")}</span>
                                            {step.ingredients.map(
                                              (ingredient) => {
                                                const measurement =
                                                  ingredient.measurements[0] ||
                                                  {};
                                                const metricAmount =
                                                  measurement.metric_amount
                                                    ? parseFloat(
                                                        measurement.metric_amount ||
                                                          1,
                                                      )
                                                    : "";
                                                return (
                                                  <li key={ingredient.id}>
                                                    {metricAmount}{" "}
                                                    {translate(
                                                      measurement.metric_unit ||
                                                        {},
                                                      "name",
                                                      locale,
                                                    )}{" "}
                                                    {translate(
                                                      ingredient,
                                                      "name",
                                                      locale,
                                                    )}
                                                  </li>
                                                );
                                              },
                                            )}
                                          </ul>
                                        )}
                                      </div>
                                    );
                                  },
                                )}
                            </div>
                          </div>
                        </TabPanel>

                        <TabPanel className={cx("TabPane", s.TabPane)}>
                          <div className={cx("TabContainer", s.TabContainer)}>
                            <div className={cx("row", s.Tools)}>
                              {uniqObjArray(
                                activeRecipe.timeline.steps.flatMap(
                                  (s) => s.equipments,
                                ),
                              ).map((equipment) => {
                                return (
                                  <div className="rowItem" key={equipment.id}>
                                    <Image
                                      src={imageUrl(equipment.images[0])}
                                      width="400"
                                      height="400"
                                      fill="responsive"
                                      objectFit="cover"
                                      alt={equipment.name}
                                    />
                                    <h4>{equipment.name}</h4>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </TabPanel>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                </Tabs>
              </div>
            </div>
          </Container>
        </Section>
        <Section className={cx(s.cocktailInfoSection, "b-greige p0")}>
          <Container>
            <div className={cx("row__xl flex flex-wrap space-around", s.Info)}>
              <div className={cx("split-item-50__d rowItem__xl")}>
                <h3 className="Heading Blue Light">{`The ${cocktail.name} Cocktail`}</h3>
                <p
                  className=""
                  itemProp="description"
                  dangerouslySetInnerHTML={{
                    __html:
                      description ||
                      `Learn how to make the ${cocktail.name} cocktail from this recipe.`,
                  }}
                ></p>
              </div>

              <div className={cx("split-item-50__d rowItem__xl")}>
                {cocktail.flavors && cocktail.flavors.length > 0 ? (
                  <div className={cx(s.TasteProfile)}>
                    <h3 className="Heading Blue Light">
                      {t("Tasting Profile")}
                    </h3>
                    {cocktail.flavors.map(({ value, flavor: { name } }) => {
                      return (
                        <div key={name}>
                          <p className="">{name}</p>
                          <span className={cx(s.progressBar)}>
                            <div style={{ width: `calc(${value} * 20%)` }}>
                              <div></div>
                            </div>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <></>
                )}

                {activeRecipe.timeline.video_embed_id &&
                activeRecipe.timeline.video_embed_id != "null" ? (
                  activeRecipe.timeline.video_embed_id.match(
                    /^(https?:\/\/|www\.)((youtube\.com|youtu\.be|vimeo\.com)\/)/i,
                  ) ? (
                    <Link
                      href={activeRecipe.timeline.video_embed_id}
                      className="Button Primary Blue Full mt32"
                      target="_blank"
                      onClick={(e) => {
                        k;
                        trackEvent();
                        if (forceLogin) {
                          e.preventDefault();
                          setLoginModalState({
                            show: true,
                            message: t(
                              "Please log in or register an account to watch more videos",
                            ),
                            index: 0,
                          });
                        }
                      }}
                    >
                      {t("Play recipe")}
                    </Link>
                  ) : (
                    <Link
                      href={`/cocktails/${cocktail.slug}/play`}
                      className="Button Primary Blue Full mt32"
                      onClick={(e) => {
                        trackEvent();
                        if (forceLogin) {
                          e.preventDefault();
                          setLoginModalState({
                            show: true,
                            message: t(
                              "Please log in or register an account to watch more videos",
                            ),
                            index: 0,
                          });
                        }
                      }}
                    >
                      {t("Play recipe")}
                    </Link>
                  )
                ) : (
                  <></>
                )}

                {!horeca && (
                  <div>
                    <br />
                    <br />
                    <h3 className={cx(s.reviewHeader, "Heading Blue Light")}>
                      {t("Reviews")}
                    </h3>
                    {cocktail.reviews.length > 0 ? (
                      <>
                        <Stars
                          score={cocktail.averageScore}
                          className={cx(r.avgM)}
                        />
                        <p className={cx(r.starCount)}>
                          <a href="#reviews">
                            {cocktail.averageScore} out of{" "}
                            {cocktail.reviews.length} reviews
                          </a>
                        </p>
                        <br />
                      </>
                    ) : (
                      <div>
                        <p className="">
                          {t(
                            "There is currently no reviews for this cocktail yet",
                          )}
                          ...
                        </p>
                      </div>
                    )}
                    {session?.user ? (
                      <button
                        className={cx("Button Hollow")}
                        onClick={() => setShowCreateReviewModal(true)}
                      >
                        {t("Add a review")}
                      </button>
                    ) : (
                      <button
                        className={cx("Button Hollow Blue")}
                        onClick={() =>
                          setLoginModalState({
                            show: true,
                            index: 0,
                            message: t(
                              "Please log in or register an account to add a review",
                            ),
                          })
                        }
                      >
                        {t("Add a review")}
                      </button>
                    )}
                    {cocktail.reviews.length > 0 && (
                      <a className="ml24" href="#reviews">
                        {t("Read reviews")}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Container>
        </Section>
        {!horeca && (
          <RelatedCocktails
            cocktail={cocktail}
            setLoginModalState={setLoginModalState}
            setShowCreateListModal={setShowCreateListModal}
            {...props}
          />
        )}
        {!horeca && (
          <Section id="reviews">
            <Container>
              <h2 className="Heading">Reviews ({cocktail.reviews.length})</h2>
              <div>
                <Stars score={cocktail.averageScore} className={cx(r.avgL)} />
                <div className={cx(r.starCount)}>
                  {cocktail.averageScore} out of {cocktail.reviews.length}{" "}
                  reviews
                </div>
                <br />
                <br />
                {cocktail.reviews.length > 0 ? (
                  cocktail.reviews.map((review, index) => {
                    return (
                      <div className={cx(r.reviewItem)} key={index}>
                        <div className={cx(r.reviewImg)}>
                          {review.hide_name ? (
                            <>A</>
                          ) : (
                            <>
                              {(
                                review.user.first_name ||
                                review.user.display_name ||
                                ""
                              ).charAt(0)}
                            </>
                          )}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                          >
                            <path d="M256 0C292.8 0 324.8 20.7 340.9 51.1C373.8 40.1 410.1 48.96 437 74.98C463 101 470.1 138.2 460.9 171.1C491.3 187.2 512 219.2 512 256C512 292.8 491.3 324.8 460.9 340.9C471 373.8 463 410.1 437 437C410.1 463 373.8 470.1 340.9 460.9C324.8 491.3 292.8 512 256 512C219.2 512 187.2 491.3 171.1 460.9C138.2 471 101 463 74.98 437C48.96 410.1 41 373.8 51.1 340.9C20.7 324.8 0 292.8 0 256C0 219.2 20.7 187.2 51.1 171.1C40.1 138.2 48.96 101 74.98 74.98C101 48.96 138.2 41 171.1 51.1C187.2 20.7 219.2 0 256 0V0zM352.1 224.1C362.3 215.6 362.3 200.4 352.1 191C343.6 181.7 328.4 181.7 319 191L224 286.1L184.1 247C175.6 237.7 160.4 237.7 151 247C141.7 256.4 141.7 271.6 151 280.1L207 336.1C216.4 346.3 231.6 346.3 240.1 336.1L352.1 224.1z" />
                          </svg>
                        </div>
                        <div className={cx(r.reviewContent)}>
                          <div className={cx(r.reviewUser)}>
                            {review.hide_name ? (
                              <div>
                                {t("Anonymous")}
                                <span>{t("Verified user")}</span>
                              </div>
                            ) : (
                              <div>
                                {
                                  (
                                    review.user.first_name ||
                                    review.user.display_name ||
                                    ""
                                  ).split(" ")[0]
                                }
                                <span>{t("Verified user")}</span>
                              </div>
                            )}
                          </div>
                          <Stars score={review.score} />
                          <div className={cx(r.reviewComment)}>
                            {review.comment}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p>{t("No reviews yet")}</p>
                )}
              </div>
              {session?.user ? (
                <button
                  className={cx("Button Primary Blue", r.addReviewBtn)}
                  onClick={() => setShowCreateReviewModal(true)}
                >
                  {t("Add a review")}
                </button>
              ) : (
                <button
                  className={cx("Button Primary Blue", r.addReviewBtn)}
                  onClick={() =>
                    setLoginModalState({
                      show: true,
                      index: 0,
                      message:
                        "Please log in or register an account to add a review",
                    })
                  }
                >
                  {t("Add a review")}
                </button>
              )}
            </Container>
          </Section>
        )}
      </article>
      <CreateReviewModal
        item={cocktail}
        type="cocktail"
        title={cocktail.name}
        onClose={() => setShowCreateReviewModal(false)}
        show={showCreateReviewModal}
      />
      <CreateListModal
        onClose={() => setShowCreateListModal(false)}
        show={showCreateListModal}
      />

      <OutsideClickHandler
        onOutsideClick={() => {
          setProductDetailState({ ...productDetailState, open: false });
        }}
      >
        <ProductDetailModal
          onClose={() => {
            setProductDetailState({ ...productDetailState, open: false });
          }}
          {...productDetailState}
        />
      </OutsideClickHandler>
    </>
  ) : (
    <></>
  );
};
